import {
	Box,
	Button,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { useAdminUploaderContext } from 'containers/pages/CANguard/context/AdminUploaderProvider';
import { useGuardRoleContext } from 'containers/pages/CANguard/context/GuardRoleProvider';
import { GuardTabs } from 'pages/CANguard/CANguard';
import React, { useEffect, useCallback } from 'react';
import StatusBar from '@uppy/status-bar';
import '@uppy/core/dist/style.min.css';
import '@uppy/status-bar/dist/style.min.css';

interface UploadTabProps {
	goToTab: (tab: GuardTabs) => void;
}

const UploadTab: React.FC<UploadTabProps> = (props) => {
	const { isAdmin } = useGuardRoleContext();
	const { uppyInstances, setUppyInstances, openDashboard } =
		useAdminUploaderContext();

	const setupStatusBar = useCallback(
		(uppyInstance: any, targetId: string) => {
			if (!uppyInstance.getState().statusBarInitialized) {
				uppyInstance.use(StatusBar, {
					target: `#${targetId}`,
					hideAfterFinish: false,
					showProgressDetails: true,
				});
				uppyInstance.setState({ statusBarInitialized: true }); // Set the status bar as initialized
			}
		},
		[],
	);

	const clearUppyInstances = () => {
		const filteredUppyInstances: { [key: string]: any } = {};

		Object.entries(uppyInstances).forEach(([patrolId, uppyInstance]) => {
			const uploads = uppyInstance.getFiles();
			const hasIncompleteUploads = uploads.some(
				(file: any) => file.progress.uploadComplete === false,
			);

			if (hasIncompleteUploads) {
				const patrol = uppyInstance.getState().meta
					.patrol as ScheduleAssignment;
				const targetId = `uppy-${patrol.schedule_assignment_id}`;
				setupStatusBar(uppyInstance, targetId);
				filteredUppyInstances[patrolId] = uppyInstance;
			}
		});

		// Update the uppyInstances with the filtered instances
		setUppyInstances(filteredUppyInstances);
	};

	useEffect(() => {
		if (!isAdmin) {
			return;
		}

		Object.entries(uppyInstances).forEach(([patrolId, uppyInstance]) => {
			const patrol = uppyInstance.getState().meta
				.patrol as ScheduleAssignment;
			const targetId = `uppy-${patrol.schedule_assignment_id}`;
			setupStatusBar(uppyInstance, targetId);
		});

		// Cleanup function to remove Uppy plugins
		return () => {};
	}, [isAdmin, uppyInstances, setupStatusBar]);

	if (!isAdmin) {
		return null;
	}

	return (
		<Box width={'100%'}>
			<Box
				sx={{
					width: '100%',
					p: 2,
					display: 'flex',
					justifyContent: 'space-between',
					gap: 2,
					flexWrap: 'wrap',
				}}
			>
				<Typography variant="h5" textAlign={'center'}>
					Upload Manager
				</Typography>
				<Button onClick={clearUppyInstances} variant="text">
					Clear Completed & Empty
				</Button>
			</Box>
			<TableContainer component={Paper}>
				<Table sx={{ minWidth: 650 }}>
					<TableHead>
						<TableRow>
							<TableCell align="left">Patrol ID</TableCell>
							<TableCell align="left">Guard Name</TableCell>
							<TableCell align="left">Customer</TableCell>
							<TableCell align="left">Address</TableCell>
							<TableCell align="left">Date</TableCell>
							<TableCell align="left">Status</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{Object.entries(uppyInstances).length === 0 && (
							<TableRow>
								<TableCell colSpan={6}>
									<Typography
										variant="h6"
										fontWeight={300}
										color={'warning.light'}
									>
										No uploads in progress. Ongoing DDS
										uploads will appear here.
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{Object.entries(uppyInstances).map(
							([patrolId, uppyInstance]) => {
								const patrol = uppyInstance.getState().meta
									.patrol as ScheduleAssignment;
								return (
									<TableRow
										key={patrolId}
										sx={{
											cursor: 'pointer',
										}}
										onClick={() =>
											openDashboard(uppyInstance)
										}
									>
										<TableCell align="left">
											{patrolId}
										</TableCell>
										<TableCell align="left">
											{patrol.full_name}
										</TableCell>
										<TableCell align="left">
											{patrol.cust_name}
										</TableCell>
										<TableCell align="left">
											{patrol.address}
										</TableCell>
										<TableCell align="left">
											{patrol.work_date
												.toString()
												.substring(0, 10)}
										</TableCell>
										<TableCell
											align="left"
											sx={{ minWidth: '300px' }}
										>
											<div
												id={`uppy-${patrol.schedule_assignment_id}`}
											/>
										</TableCell>
									</TableRow>
								);
							},
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

export default UploadTab;
